import React, { useEffect, forwardRef } from "react";
import Chart from "chart.js";

const OccupancyGraph = forwardRef(({ labels = [], companyData = [] }, ref) => {
  useEffect(() => {
    const ctx = ref.current.getContext("2d");

    const datasets = companyData.flatMap((c) => [
      // Bar 1: Proposed occupancy
      {
        type: "bar",
        label: `${c.name} – Occupancy (Proposed)`,
        data: c.barData,
        backgroundColor: c.bar2BackgroundColor,
        borderColor: c.bar2BorderColor,
        borderWidth: 1,
        yAxisID: "y-occupancy",
      },
      // Bar 2: Actual occupancy
      {
        type: "bar",
        label: `${c.name} – Occupancy (Actual)`,
        data: c.bar2Data,
        backgroundColor: c.barBackgroundColor,
        borderColor: c.barBorderColor,
        borderWidth: 1,
        yAxisID: "y-occupancy",
      },
      // Line 1: Cost per head (Proposed)
      {
        type: "line",
        label: `${c.name} – Cost/Head (Proposed)`,
        data: c.lineData,
        borderColor: c.line2BorderColor,
        fill: false,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 3,
        yAxisID: "y-cost",
      },
      // Line 2: Cost per head (Actual)
      {
        type: "line",
        label: `${c.name} – Cost/Head (Actual)`,
        data: c.line2Data,
        borderColor: c.lineBorderColor,
        fill: false,
        borderWidth: 2,
        pointRadius: 3,
        yAxisID: "y-cost",
      },
    ]);

    const chartInstance = new Chart(ctx, {
      type: "bar",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,

        // Add some space between legend and chart
        layout: { padding: { top: 12 } },

        legend: {
          position: "top",
          labels: {
            boxWidth: 20,
            padding: 14,
            // Make line legends outlined (and dashed if dataset is dashed)
            generateLabels: function (chart) {
              // start from the default set
              const original =
                Chart.defaults.global.legend.labels.generateLabels;
              const labels = original.call(this, chart);

              labels.forEach((label) => {
                const ds = chart.data.datasets[label.datasetIndex];
                if (!ds) return;

                if (ds.type === "line") {
                  // outline only
                  label.fillStyle = "rgba(0,0,0,0)"; // transparent fill
                  label.strokeStyle = Array.isArray(ds.borderColor)
                    ? ds.borderColor[0]
                    : ds.borderColor;
                  label.lineWidth = ds.borderWidth || 2;

                  // if the line is dashed, make the legend outline dashed too
                  if (ds.borderDash && ds.borderDash.length) {
                    label.lineDash = ds.borderDash.slice(); // e.g., [6, 4]
                  } else {
                    label.lineDash = [];
                  }
                }
                // bars are left as-is (solid color boxes)
              });

              return labels;
            },
          },
        },

        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (tooltipItem, data) {
              const ds = data.datasets[tooltipItem.datasetIndex];
              const val = tooltipItem.yLabel;
              if (ds.yAxisID === "y-occupancy") return `${ds.label}: ${val}%`;
              return `${ds.label}: R${val}`;
            },
          },
        },

        scales: {
          yAxes: [
            {
              id: "y-occupancy",
              position: "left",
              ticks: {
                beginAtZero: true,
                min: 0,
                max: 100,
                stepSize: 10,
                callback: (v) => `${v}%`,
              },
              scaleLabel: {
                display: true,
                labelString: "Occupancy (%)",
              },
            },
            {
              id: "y-cost",
              position: "right",
              gridLines: { drawOnChartArea: false },
              ticks: { beginAtZero: true },
              scaleLabel: {
                display: true,
                labelString: "Cost per head",
              },
            },
          ],
          xAxes: [{ stacked: false }],
        },
      },
    });

    return () => {
      chartInstance.destroy();
    };
  }, [labels, companyData, ref]);

  return (
    <div style={{ width: "100%", height: "100%", backgroundColor: "white" }}>
      <canvas ref={ref} width={"100%"} height={"20rem"} />
    </div>
  );
});

export default React.memo(OccupancyGraph);
